/**
 * ShopOverrideDialog - Individual shop-specific threshold override
 * Used for setting a threshold for one specific product + shop combination
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Store, Trash2 } from "lucide-react";
import { SHOP_TYPE_STYLES } from "./constants";

export default function ShopOverrideDialog({
  open,
  onOpenChange,
  product,
  shop,
  existingOverride,
  minQty,
  maxQty,
  onMinChange,
  onMaxChange,
  saving,
  onSave,
  onDelete,
}) {
  const isValid = minQty && maxQty;
  const hasExistingOverride = existingOverride?.threshold_source === "shop_override";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Store className="w-5 h-5 text-purple-600" />
            Shop-Specific Override
          </DialogTitle>
          <DialogDescription>
            Set a specific threshold for this product at this shop. This overrides both product-level custom thresholds and defaults.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Product Info */}
          {product && (
            <div className="bg-muted p-3 rounded">
              <div className="font-medium">{product.product_name}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {product.brand_name} · {product.group_name}
              </div>
            </div>
          )}

          {/* Shop Info */}
          {shop && (
            <div className="bg-purple-50 p-3 rounded border border-purple-200">
              <div className="flex items-center gap-2">
                <Store className="w-4 h-4 text-purple-600" />
                <span className="font-medium text-purple-800">{shop.shop_name}</span>
                <Badge variant="outline" className={`text-[8px] ${SHOP_TYPE_STYLES[shop.shop_type || "normal"]}`}>
                  {shop.shop_type === "warehouse" ? "WAREHOUSE" : "NORMAL"}
                </Badge>
              </div>
              {existingOverride && (
                <div className="text-xs text-purple-700 mt-2">
                  Current: {existingOverride.min_qty}-{existingOverride.max_qty} (
                  {existingOverride.threshold_source === "shop_override" ? "Shop Override" :
                   existingOverride.threshold_source === "custom" ? "Product Custom" : "Default"})
                </div>
              )}
            </div>
          )}

          {/* Override Values */}
          <div className="p-3 rounded-lg border bg-purple-50/50">
            <div className="text-sm font-medium text-purple-800 mb-3">New Override Values</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Min Qty</Label>
                <Input
                  type="number"
                  min="0"
                  value={minQty}
                  onChange={(e) => onMinChange(e.target.value)}
                  placeholder="e.g., 5"
                  className="h-9 mt-1"
                  data-testid="shop-override-min"
                />
              </div>
              <div>
                <Label className="text-xs">Max Qty</Label>
                <Input
                  type="number"
                  min="0"
                  value={maxQty}
                  onChange={(e) => onMaxChange(e.target.value)}
                  placeholder="e.g., 20"
                  className="h-9 mt-1"
                  data-testid="shop-override-max"
                />
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="text-xs text-muted-foreground bg-amber-50 p-2 rounded border border-amber-200">
            <strong>Priority Order:</strong>
            <ol className="mt-1 space-y-0.5 list-decimal list-inside">
              <li className="text-purple-700 font-medium">Shop-Specific Override (highest)</li>
              <li>Product Custom Threshold by shop type</li>
              <li>Default Threshold by shop type (lowest)</li>
            </ol>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          {hasExistingOverride && (
            <Button
              variant="destructive"
              size="sm"
              onClick={onDelete}
              disabled={saving}
              className="mr-auto"
              data-testid="delete-shop-override-btn"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Remove Override
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || !isValid}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="save-shop-override-btn"
          >
            {saving ? "Saving..." : "Save Override"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
